/**
 * intelligenceFiles.ts — typed wire contracts for the Phibian Intelligence
 * attachment evidence-source domain.
 *
 * Every schema here re-declares a shape the API domain already serves (see
 * `apps/api/src/domains/intelligence/files/{types,validate}.ts`, mission
 * lane M1). The API package is deliberately NOT imported — same drift-loudly
 * discipline as the other intelligence contract modules. Source of truth for
 * each schema is named in its doc comment. No HTTP route paths: the upload
 * route is lead-owned; these are contract types only.
 *
 * The hard rules this contract pins client-side:
 *
 *   - FILE CONTENTS ARE NEVER CARRIED. The envelope has no content, no
 *     extracted text, no byte payload of any kind — only a reference
 *     (`fileId`) plus the sha256 `contentHash` of the bytes. Tools cite a
 *     source via `fileEvidenceCitationSchema`: ref + hash + non-identifying
 *     type facts, and nothing else.
 *
 *   - FILE NAME FREE-TEXT IS RESTRICTED. The envelope carries NO name field;
 *     unknown name-shaped keys fail the parse (`phi_field_detected` mirror),
 *     as do secret-shaped keys (`sensitive_field_detected` mirror) — the
 *     files/validate.ts tripwires re-declared verbatim.
 *
 *   - `fileId` is OPAQUE and server-derived ('file_' + 64 hex). The client
 *     pins the SHAPE only; the derivation check (envelope → id) is
 *     server-side (files/types.ts deriveFileId) and is not re-implemented
 *     here.
 */
import { z } from 'zod';
import { sensitivityClassificationSchema } from './intelligencePersonalization.js';
// ---------------------------------------------------------------------------
// Typed domain vocabulary (files/types.ts)
// ---------------------------------------------------------------------------
/** The six attachment kinds Intelligence accepts as evidence sources. */
export const fileSourceTypeSchema = z.enum(['pdf', 'docx', 'xlsx', 'csv', 'image', 'text']);
export const FILE_SOURCE_TYPES = fileSourceTypeSchema.options;
/**
 * Retention classes for attachment evidence — typed POLICY keys, weakest to
 * longest: TRANSIENT, SESSION, ORGANIZATION, COMPLIANCE. Mapping each class
 * to a physical deletion schedule is the lead-owned storage lane's contract.
 */
export const fileRetentionClassSchema = z.enum([
    'TRANSIENT',
    'SESSION',
    'ORGANIZATION',
    'COMPLIANCE',
]);
export const FILE_RETENTION_CLASSES = fileRetentionClassSchema.options;
/** Per-source-type byte-size ceilings. `byteSize` above the ceiling is rejected. */
export const FILE_SIZE_LIMITS_BYTES = {
    pdf: 25 * 1024 * 1024,
    docx: 15 * 1024 * 1024,
    xlsx: 15 * 1024 * 1024,
    csv: 10 * 1024 * 1024,
    image: 10 * 1024 * 1024,
    text: 1 * 1024 * 1024,
};
/**
 * The ONLY mime types a sourceType may declare (files/types.ts). A free-form
 * mimeType fails the parse — closing the "pdf that is really an executable"
 * hole at the contract level.
 */
export const ALLOWED_MIME_TYPES = {
    pdf: ['application/pdf'],
    docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    csv: ['text/csv'],
    image: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    text: ['text/plain', 'text/markdown'],
};
// ---------------------------------------------------------------------------
// Tripwire patterns — re-declared VERBATIM from files/validate.ts.
// ---------------------------------------------------------------------------
/** Opaque fileId shape: 'file_' + 64 lowercase hex (sha256Hex output). */
const FILE_ID_SHAPE = /^file_[0-9a-f]{64}$/;
/** sha256 hex, exactly 64 chars — the evidence-manifest shape. */
const HASH64_HEX = /^[0-9a-f]{64}$/i;
/** RFC 4122 shape (8-4-4-4-12 hex), any version/variant — same as verify.ts. */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * Name-shaped keys are where free-text PHI would live (file names, client
 * labels, display titles). The envelope has NO name field by contract, so
 * any unknown key that looks name-shaped fails the parse.
 */
export const NAME_SHAPED_KEY = /(name|title|description|label|display|subject|client|worker)/i;
/** Secret-shaped keys, same heuristic as evidence/verify.ts. */
export const SENSITIVE_KEY = /(secret|token|password|credential|api[-_.]?key|authorization|private[-_.]?key)/i;
/** The declared envelope keys (FileEvidenceSource). */
const KNOWN_KEYS = new Set([
    'fileId',
    'tenantOrgId',
    'uploaderUserId',
    'sourceType',
    'mimeType',
    'byteSize',
    'contentHash',
    'sensitivityClassification',
    'requiredPermissions',
    'retentionClass',
    'createdAt',
    'provenanceRefs',
    'auditRef',
]);
// ---------------------------------------------------------------------------
// The envelope
// ---------------------------------------------------------------------------
/**
 * The metadata envelope every attachment evidence source carries (files/
 * types.ts FileEvidenceSource). Strictly metadata + refs: no bytes, no
 * extracted content, no free-text names. Every field is a deterministic,
 * typed fact established at ingest.
 *
 * Unknown fields that are neither name- nor secret-shaped are tolerated
 * (forward-compatible envelope evolution) — exactly like validate.ts.
 */
export const fileEvidenceSourceSchema = z
    .object({
    /** Opaque, server-derived reference id ('file_' + 64 hex). A token, never parsed. */
    fileId: z.string().regex(FILE_ID_SHAPE),
    /** The tenant the attachment belongs to. RLS/tenant scoping keys on this. */
    tenantOrgId: z.string().regex(UUID_SHAPE),
    /** The authenticated user who uploaded it (resolved server-side at ingest). */
    uploaderUserId: z.string().regex(UUID_SHAPE),
    /** Claimed kind of the attachment. Must match mimeType's declared set. */
    sourceType: fileSourceTypeSchema,
    /** Declared MIME type, constrained to ALLOWED_MIME_TYPES[sourceType]. */
    mimeType: z.string().min(1),
    /** Byte size as measured at ingest. Positive integer, ≤ the per-type limit. */
    byteSize: z.number().int().positive(),
    /** sha256 (64-char lowercase hex) of the FILE BYTES — evidence-manifest shape. */
    contentHash: z.string().regex(HASH64_HEX),
    /** Classification across the org-wide vocabulary (memory.ts): internal | sensitive | restricted. */
    sensitivityClassification: sensitivityClassificationSchema,
    /** Permission keys a caller must hold (via can()) to READ this source — a gate, never a grant. */
    requiredPermissions: z.array(z.string().min(1)).min(1),
    /** Typed retention policy key (see FILE_RETENTION_CLASSES). */
    retentionClass: fileRetentionClassSchema,
    /** Server clock (ms epoch) at ingest. */
    createdAt: z.number().int().min(0),
    /** Refs only — evidence-manifest entry ids, tool-call ids, retrieval ids. Never names, never content. */
    provenanceRefs: z.array(z.string().min(1)),
    /** Reference to the audit row that recorded the ingest (handoff constraint 6). */
    auditRef: z.string().min(1),
})
    .passthrough()
    .superRefine((env, ctx) => {
    if (FILE_SOURCE_TYPES.includes(env.sourceType)) {
        const allowed = ALLOWED_MIME_TYPES[env.sourceType];
        if (!allowed.includes(env.mimeType)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `mimeType must be one of the declared types for a ${env.sourceType} source`,
                path: ['mimeType'],
            });
        }
        if (env.byteSize > FILE_SIZE_LIMITS_BYTES[env.sourceType]) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `byteSize exceeds the ${FILE_SIZE_LIMITS_BYTES[env.sourceType]} byte limit for a ${env.sourceType} source`,
                path: ['byteSize'],
            });
        }
    }
    for (const key of Object.keys(env)) {
        if (KNOWN_KEYS.has(key))
            continue;
        if (NAME_SHAPED_KEY.test(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `field '${key}' is name-shaped free text and does not belong in attachment metadata (refs, not names)`,
                path: [key],
            });
        }
        else if (SENSITIVE_KEY.test(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `field '${key}' looks like a secret and does not belong in an attachment evidence envelope`,
                path: [key],
            });
        }
    }
});
// ---------------------------------------------------------------------------
// Citation — the only thing a tool may carry toward a provider
// ---------------------------------------------------------------------------
/**
 * The ONLY representation of an attachment that may travel toward a provider
 * payload: a reference + content hash plus non-identifying type facts
 * (files/types.ts citeFileEvidence). No content, no name, no tenant, no
 * uploader, no permissions. `.strict()` — adding a field here is a contract
 * change because this object is the attachment evidence boundary.
 */
export const fileEvidenceCitationSchema = z
    .object({
    fileId: z.string().regex(FILE_ID_SHAPE),
    contentHash: z.string().regex(HASH64_HEX),
    sourceType: fileSourceTypeSchema,
    mimeType: z.string().min(1),
    byteSize: z.number().int().positive(),
})
    .strict();
//# sourceMappingURL=intelligenceFiles.js.map